import { create } from "zustand";
import {
  RBACMatrixRow,
  RBACMatrixResponse,
  RBACMatrixFilters,
  RBACMatrixDetails,
} from "@/core/administration/entities/RBACMatrix";
import Exception from "@/core/exceptions/Exception";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import RBACMatrixServiceProvider from "@/app/pages/dashboard/pages/administrationPage/services/RBACMatrixServiceProvider";

const serviceProvider = RBACMatrixServiceProvider.getInstance();

export interface RBACMatrixState {
  // Data
  data: RBACMatrixResponse;
  rows: RBACMatrixRow[];

  // Filters
  searchFilter: string;
  statusFilter: string;
  criticalityFilter: string;
  frequencyFilter: string;
  sodFilter: string;

  // Pagination
  pageFilter: number;
  sizeFilter: number;

  // UI State
  loading: boolean;
  loadingRoleDetails: boolean;
  publishingRole: boolean;
  failedFetchingRBACMatrixException: Exception | null;
  failedFetchingRoleDetailsException: Exception | null;
  failedPublishingRoleException: Exception | null;

  // Actions
  setRows: (rows: RBACMatrixRow[]) => void;
  setData: (data: RBACMatrixResponse) => void;

  // Filter setters
  setSearchFilter: (search: string) => void;
  setStatusFilter: (status: string) => void;
  setCriticalityFilter: (criticality: string) => void;
  setFrequencyFilter: (frequency: string) => void;
  setSodFilter: (sod: string) => void;

  // Pagination setters
  setPageFilter: (page: number) => void;
  setSizeFilter: (size: number) => void;

  // Main action
  fetchRBACMatrix: (filters?: RBACMatrixFilters) => Promise<void>;

  fetchRBAACMatrixDetailsById: (
    id: string,
  ) => Promise<RBACMatrixDetails | undefined>;
  publishRoleById: (id: string) => Promise<boolean>;

  // Reset filters
  resetFilters: () => void;
}

const initialData: RBACMatrixResponse = {
  code: "00",
  message: "Le traitement s'est déroulé avec succès.",
  data: {
    items: [],
    totalElements: 0,
    totalPages: 1,
    first: true,
    last: true,
  },
};

export const useRBACMatrixStore = create<RBACMatrixState>((set, get) => ({
  // Initial state
  data: initialData,
  rows: [],

  // Initial filters
  searchFilter: "",
  statusFilter: "",
  criticalityFilter: "",
  frequencyFilter: "",
  sodFilter: "",

  // Initial pagination
  pageFilter: 0,
  sizeFilter: 20,

  // Initial UI state
  loading: false,
  loadingRoleDetails: false,
  publishingRole: false,
  failedFetchingRBACMatrixException: null,
  failedFetchingRoleDetailsException: null,
  failedPublishingRoleException: null,

  // Actions
  setRows: (rows) => set({ rows }),
  setData: (data) => set({ data }),

  // Filter setters
  setSearchFilter: (search) => set({ searchFilter: search }),
  setStatusFilter: (status) => set({ statusFilter: status }),
  setCriticalityFilter: (criticality) =>
    set({ criticalityFilter: criticality }),
  setFrequencyFilter: (frequency) => set({ frequencyFilter: frequency }),
  setSodFilter: (sod) => set({ sodFilter: sod }),

  // Pagination setters
  setPageFilter: (page) => set({ pageFilter: page }),
  setSizeFilter: (size) => set({ sizeFilter: size }),

  // Main action - call repository via service provider
  fetchRBACMatrix: async (filters) => {
    try {
      set({ loading: true, failedFetchingRBACMatrixException: null });

      const current = get();
      const payload: RBACMatrixFilters = {
        search: filters?.search ?? current.searchFilter,
        status: filters?.status ?? current.statusFilter,
        criticality: filters?.criticality ?? current.criticalityFilter,
        frequency: filters?.frequency ?? current.frequencyFilter,
        sod: filters?.sod ?? current.sodFilter,
        page: filters?.page ?? current.pageFilter,
        size: filters?.size ?? current.sizeFilter,
      };

      const result = await serviceProvider
        .getRepository()
        .getRBACMatrix(payload);

      if (result.success && result.data) {
        const response = result.data;
        set({
          data: response,
          rows: response.data.items,
        });
      } else {
        set({ failedFetchingRBACMatrixException: result.exception ?? null });
      }
    } catch (error) {
      console.error("Erreur lors du chargement de la matrice RBAC :", error);
      set({
        failedFetchingRBACMatrixException: {
          type: DefaultExceptionTypes.UNEXPECTED_ERROR,
          message: "Erreur lors du chargement des données",
        },
      });
    } finally {
      set({ loading: false });
    }
  },

  fetchRBAACMatrixDetailsById: async (id) => {
    try {
      set({
        loadingRoleDetails: true,
        failedFetchingRoleDetailsException: null,
      });

      const result = await serviceProvider
        .getRepository()
        .getRoleDetailsById(id);

      if (result.success && result.data) {
        const response = result.data;
        set({ loadingRoleDetails: false });
        return response.data;
      } else {
        set({ failedFetchingRoleDetailsException: result.exception ?? null });
        return undefined;
      }
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails de la matrice RBAC :",
        error,
      );
      set({
        failedFetchingRoleDetailsException: {
          type: DefaultExceptionTypes.UNEXPECTED_ERROR,
          message: "Erreur lors du chargement des données",
        },
      });
    } finally {
      set({ loadingRoleDetails: false });
    }
  },

  publishRoleById: async (id) => {
    try {
      set({
        publishingRole: true,
        failedPublishingRoleException: null,
      });

      const result = await serviceProvider.getRepository().publishRoleById(id);
      if (result.success) {
        return true;
      }

      set({ failedPublishingRoleException: result.exception ?? null });
      return false;
    } catch (error) {
      console.error("Erreur lors de la publication du role RBAC :", error);
      set({
        failedPublishingRoleException: {
          type: DefaultExceptionTypes.UNEXPECTED_ERROR,
          message: "Erreur lors de la publication du role",
        },
      });
      return false;
    } finally {
      set({ publishingRole: false });
    }
  },

  // Reset all filters to initial state
  resetFilters: () => {
    set({
      searchFilter: "",
      statusFilter: "",
      criticalityFilter: "",
      frequencyFilter: "",
      sodFilter: "",
      pageFilter: 0,
    });
  },
}));
